import { AssetType, ObservationSchema, ProjectUserSummary } from '@ynomia/core/dist/blueprint';
import { RawObservationRecord } from '../config/types';
import client from '../services/Client';

/**
 * This gets the latest entry in the asset history.
 */
export const getFirstObservationWithType = (
  observations: RawObservationRecord[] | undefined,
  observationType: string | undefined,
) => observations?.find(observation => observation.type === observationType);

/**
 * This gets the last created observation
 */
export const getLastCreatedObservationWithUser = (
  observations: RawObservationRecord[] | undefined,
) => {
  const sortedByCreated = observations?.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  );
  // Return the first user-generated observation if observationType is undefined
  return sortedByCreated?.find(observation => observation.userID);
};

export const getFirstObservationExists = (
  observations: RawObservationRecord[] | undefined,
  observationType: string | undefined,
) => {
  const observation = getFirstObservationWithType(observations, observationType);
  if (!observation) return 'No';
  return 'Yes';
};

export const getFirstObservationDate = (
  observations: RawObservationRecord[] | undefined,
  observationType: string | undefined,
) => {
  const observation = getFirstObservationWithType(observations, observationType);
  if (!observation) return '';
  return observation.date;
};

export const getLastCreatedObservationCreatedDate = (
  observations: RawObservationRecord[] | undefined,
) => {
  const observation = getLastCreatedObservationWithUser(observations);
  if (!observation) return '';
  return observation.created;
};

export const getLastCreatedObservationUser = (
  observations: RawObservationRecord[] | undefined,
  projectUserListKeyedById: Map<string, ProjectUserSummary>,
) => {
  const observation = getLastCreatedObservationWithUser(observations);
  if (!observation) return '';
  return projectUserListKeyedById.get(observation.userID!)?.name || 'Ynomia';
};

export const getFirstObservationField = (
  observations: RawObservationRecord[] | undefined,
  observationType: string | undefined,
  field: string,
) => {
  const observation = getFirstObservationWithType(observations, observationType);
  if (!observation) return '';
  return observation.fields?.[field];
};

export const getFirstObservationUser = (
  observations: RawObservationRecord[] | undefined,
  observationType: string | undefined,
  projectUserListKeyedById: Map<string, ProjectUserSummary>,
) => {
  const observation = getFirstObservationWithType(observations, observationType);
  if (!observation) return '';
  const user = projectUserListKeyedById.get(observation.userID!)?.name;
  return user;
};

export const determinePermittedObservations = (at: AssetType): ObservationSchema[] => (
  (at.observations || [])
    ?.filter(obs => !!obs.isAction)
    ?.filter(obs => (
      client.session.checkFeature(
        `assets.flags.observation_visibility.${obs.id}`,
        true,
      )
    ))
);
